import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { IUser } from 'src/app/core/models/user.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { ClientService } from 'src/app/core/services/client.service';
import { LicensePlateService } from 'src/app/core/services/license-plate.service';
import { UserService } from 'src/app/core/services/user.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-license-plates-list',
  templateUrl: './license-plates-list.component.html',
  styleUrls: ['./license-plates-list.component.css'],
})
export class LicensePlatesListComponent implements OnInit {
  @Input() darkMode: boolean;
  @Input() role: string;
  @Input() userId: string;
  @Input() clientId: string;
  @Input() customerId: string;
  @Output() reloadLicensePlates = new EventEmitter();

  private dialogOptions: NgbModalOptions = {
    backdropClass: 'dark-backdrop',
    centered: true,
    windowClass: 'window-modal',
  };

  urlParams: string;

  addedLicensePlates: any;
  availableLicensePlates: any;

  searchParam: string;
  licensePlatesSearch: any;
  searchParamAll: string;
  licensePlatesSearchAll: any;
  showMyContainer = false;
  selectLicensePlate: any = 'Select License Plates...';

  toggleAddLogo = false;

  isUploading = false;
  uploading = false;
  status = false;

  licensePlateModal: any;

  toggleAddFrame = false;

  page = 1;
  perPage = 16;

  currentUser: IUser;

  licensePlateForm: FormGroup;
  editingMode = false; // Flag to indicate whether it's in editing mode
  editedLicensePlateIndex: number; // Index of the license plate being edited
  selectedLicensePlate: any;
  currentImageUrl: string | null = null;

  constructor(
    private modalService: NgbModal,
    private toastr: ToastrService,
    public router: Router,
    public route: ActivatedRoute,
    private authService: AuthService,
    private licensePlateService: LicensePlateService,
    private clientService: ClientService,
    private userService: UserService,
    private fb: FormBuilder,
    private translate: TranslateService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.translate.get(['global.selectLicensePlate']).subscribe((values) => {
      this.selectLicensePlate = values['global.selectLicensePlate'];
    });
    this.authService.LoggedinUser.subscribe((user) => {
      this.currentUser = user;
    });
    this.route.queryParams.subscribe((queryParams: any) => {
      this.urlParams = queryParams;
    });
    this.loadLicensePlates();
  }

  initializeForm() {
    this.licensePlateForm = this.fb.group({
      name: ['', Validators.required],
      file: [null, Validators.required],
      filePath: [''],
    });

    // Disable file control in editing mode
    if (this.editingMode) {
      this.licensePlateForm.get('file').clearValidators();
      this.licensePlateForm.get('file').updateValueAndValidity();
    }
  }

  loadLicensePlates() {
    let getLicensePlates$ = new Observable();
    let getAvailableLicensePlates$ = new Observable();

    switch (this.role) {
      case 'root':
        getLicensePlates$ = this.licensePlateService.getLicensePlates();
        break;
      case 'customer':
        if (this.currentUser.role.name === 'root') {
          getLicensePlates$ = this.licensePlateService.getCustomerLicensePlates(
            this.customerId
          );
          getAvailableLicensePlates$ =
            this.licensePlateService.getLicensePlates();
        } else {
          getLicensePlates$ = this.licensePlateService.getCustomerLicensePlates(
            this.customerId
          );
          getAvailableLicensePlates$ =
            this.licensePlateService.getCustomerLicensePlates(this.currentUser.customerId);
        }
        break;
      case 'client':
        getLicensePlates$ = this.clientService.getLicensePlates(this.clientId);
        getAvailableLicensePlates$ =
          this.licensePlateService.getCustomerLicensePlates(this.customerId);
        break;
      case 'user':
        getLicensePlates$ = this.userService.getLicensePlates(this.userId);
        getAvailableLicensePlates$ = this.clientService.getLicensePlates(
          this.clientId
        );
        break;
      default:
        break;
    }

    getLicensePlates$.subscribe((licensePlates) => {
      this.addedLicensePlates = licensePlates;
      this.licensePlatesSearchAll = licensePlates;
    });

    if (getAvailableLicensePlates$) {
      getAvailableLicensePlates$.subscribe((licensePlates) => {
        this.availableLicensePlates = licensePlates;
      });
    }
  }

  openDropdown() {
    this.showMyContainer = !this.showMyContainer;
    this.searchParam = '';
    this.licensePlatesSearch = this.availableLicensePlates;
  }

  applyFilter() {
    this.licensePlatesSearch = this.availableLicensePlates.filter((plate) =>
      plate.name.toLowerCase().includes(this.searchParam.toLowerCase())
    );
  }

  applyFilterOnAddedPlates() {
    this.licensePlatesSearchAll = this.addedLicensePlates.filter((plate) =>
      plate.name.toLowerCase().includes(this.searchParamAll.toLowerCase())
    );
  }

  clearSearch() {
    if (this.searchParamAll !== '') {
      this.searchParamAll = '';
      this.applyFilterOnAddedPlates();
    }
  }

  assignLicensePlate(licensePlateId: string) {
    const isAssigned = this.addedLicensePlates?.some(
      (plate) => plate.id === licensePlateId
    );

    if (isAssigned) {
      this.translate
        .get('licensePlates.alreadyAdded')
        .subscribe((text: string) => {
          this.toastr.error(text);
        });
      return false;
    }

    let addLicensePlate$ = new Observable();

    switch (this.role) {
      case 'customer':
        addLicensePlate$ = this.licensePlateService.addLicensePlateToCustomer(
          this.customerId,
          { licensePlateId }
        );
        break;
      case 'client':
        addLicensePlate$ = this.clientService.addLicensePlate(this.clientId, {
          licensePlateId,
        });
        break;
      case 'user':
        addLicensePlate$ = this.userService.addLicensePlate(this.userId, {
          licensePlateId,
        });
        break;
      default:
        break;
    }

    addLicensePlate$.subscribe((res) => {
      this.loadLicensePlates();
      this.reloadLicensePlates.emit();
      this.showMyContainer = !this.showMyContainer;
      this.translate
        .get('licensePlates.successAdd')
        .subscribe((text: string) => {
          this.toastr.success(text);
        });
    });
  }

  openLicensePlateModal(content, licensePlate: any) {
    this.selectedLicensePlate = licensePlate;
    this.licensePlateModal = this.modalService.open(
      content,
      this.dialogOptions
    );
  }

  showDialogAddLicensePlate(content) {
    this.editingMode = false;

    this.licensePlateModal = this.modalService.open(
      content,
      this.dialogOptions
    );
    this.licensePlateModal.result.then(
      (result) => {},
      (reason) => {
        this.licensePlateForm.reset();
      }
    );
  }

  createLicensePlate(body: FormData) {
    this.licensePlateService.addLicensePlate(body).subscribe(
      (res) => {
        this.toastr.success('License Plate added');
        this.loadLicensePlates();
        this.reloadLicensePlates.emit();
      },
      (err) => {
        this.toastr.success('Something wrong');
      }
    );

    this.licensePlateModal.close();
  }

  deleteLicensePlate(licensePlate) {
    let deleteLicensePlate$ = this.licensePlateService.deleteLicensePlate(
      licensePlate.id
    );

    switch (this.role) {
      case 'root':
        deleteLicensePlate$ = this.licensePlateService.deleteLicensePlate(
          licensePlate.id
        );
        break;
      case 'customer':
        deleteLicensePlate$ =
          this.licensePlateService.deleteLicensePlateFromCustomer(
            this.customerId,
            licensePlate.id
          );
        break;
      case 'client':
        deleteLicensePlate$ = this.clientService.removeLicensePlate(
          this.clientId,
          licensePlate.id
        );
        break;
      case 'user':
        deleteLicensePlate$ = this.userService.removeLicensePlate(
          this.userId,
          licensePlate.id
        );
        break;
      default:
        break;
    }

    deleteLicensePlate$.subscribe(
      (res) => {
        this.translate
          .get('licensePlates.deletedLicensePlate')
          .subscribe((text: string) => {
            this.toastr.success(text);
          });
        this.loadLicensePlates();
        this.reloadLicensePlates.emit();
      },
      (err) => {}
    );
    this.licensePlateModal.close();
  }

  editLicensePlate(licensePlateId: string, body) {
    this.licensePlateService.editLicensePlate(licensePlateId, body).subscribe(
      (res) => {
        this.toastr.success('License Plate updated');
        this.loadLicensePlates();
        this.reloadLicensePlates.emit();

        this.initializeForm();
      },
      (err) => {}
    );
  }

  onEditLicensePlate(content, index) {
    console.log(index);
    console.log(this.licensePlatesSearchAll[index]);
    const licensePlate = this.licensePlatesSearchAll[index];
    // Set the form values for editing
    this.licensePlateForm.setValue({
      name: licensePlate?.name,
      file: null,
      filePath: licensePlate.file?.path || '',
    });
    this.currentImageUrl = licensePlate.file?.path || '';

    this.editingMode = true;
    this.editedLicensePlateIndex = index;

    // Clear validators only for the file control
    this.licensePlateForm.get('file').clearValidators();
    this.licensePlateForm.get('file').updateValueAndValidity();

    this.licensePlateModal = this.modalService.open(
      content,
      this.dialogOptions
    );
    this.licensePlateModal.result.then(
      (result) => {},
      (reason) => {
        this.licensePlateForm.reset();
        this.editingMode = false;
        this.currentImageUrl = null;
        this.initializeForm();
      }
    );
  }

  jumpToPage(event: any) {
    this.page = event;
  }

  onSubmit() {
    if (this.editingMode) {
      // Handle editing logic here
      const licensePlate =
        this.licensePlatesSearchAll[this.editedLicensePlateIndex];

      const formData: FormData = new FormData();
      formData.append('name', this.licensePlateForm.value.name);
      this.licensePlateForm.value.file && formData.append('file', this.licensePlateForm.value.file);

      this.editLicensePlate(licensePlate.id, formData);

      // Reset the form after editing
      this.licensePlateForm.reset();
      this.licensePlateModal.close();
      this.editingMode = false;
    } else {
      const formData: FormData = new FormData();
      formData.append('name', this.licensePlateForm.value.name);
      formData.append('file', this.licensePlateForm.value.file);

      this.createLicensePlate(formData);
      // Reset the form after adding
      this.licensePlateForm.reset();
    }
  }

  fileSelectorOpen() {
    const element: HTMLElement = document.getElementById(
      'file'
    ) as HTMLElement;

    element.click();
  }

  onFileChange(event: any) {
    const file = (event.target as HTMLInputElement).files[0];
    this.licensePlateForm.patchValue({
      file: file,
      filePath: ''
    });

    const reader = new FileReader();
    reader.onload = () => {
      this.currentImageUrl = reader.result as string;
    };
    file && reader.readAsDataURL(file);
  }

  cancelEditing() {
    // Reset the form and exit editing mode
    this.licensePlateForm.reset();
    this.editingMode = false;
    this.licensePlateModal.close();
    this.initializeForm();
  }
}
